// Road-class de-emphasis: a lane-restriction layer is about the lanes, and the rest of
// the network is context. Majors keep the default black hairline; minors fade back so
// they stop competing with the data.
//
// The split is the Orbis spec's Major Road Line / Minor Road Line feature groups, read
// off `highway=`. NB this deliberately does NOT reuse `supportedHighwayTypes.js`:
// those lists answer "which highway types does feature X cover", not "what class is
// this road", and they disagree with the spec — both `track` and `road` appear in
// MAJOR_HIGHWAY_TYPES *and* MINOR_HIGHWAY_TYPES there, while the spec puts both firmly
// in Minor Road Line. Don't "unify" them; they answer different questions.
import { getPropertyEndingWith } from "../propertyExtraction";

// Orbis spec, feature_group/minor_road_line.dita. Only the minor list is enumerated:
// major is "tagged, and not one of these", so a highway type the spec adds later lands on
// the prominent side by default instead of vanishing. For reference, Major Road Line is
// motorway, trunk, primary, secondary, tertiary and their _link forms, plus residential,
// unclassified, living_street and construction.
const MINOR_ROAD_HIGHWAYS = [
  "service",
  "track",
  "road",
  "pedestrian",
  "footway",
  "cycleway",
  "path",
  "steps",
  "bridleway",
];

export const ROAD_CLASS_MAJOR = "major";
// `service` is a Minor Road Line, but the useful kind — driveways, parking aisles, depot
// roads — so it is separated out to sit one step above the rest.
export const ROAD_CLASS_SERVICE = "service";
export const ROAD_CLASS_MINOR = "minor";
// No `highway=` at all. Distinct from an unrecognised value: a way the basemap doesn't
// call a road is not a road line, and on the lane layer it is dropped outright.
export const ROAD_CLASS_UNTAGGED = "untagged";

/**
 * Classify a way from its `highway=` tag.
 *
 * Absent and unrecognised are treated differently on purpose. No tag means the basemap
 * never called this a road, so there is nothing to draw as one. An unrecognised VALUE is
 * a road whose type this code hasn't been taught yet, and fading that out — or hiding
 * it — would silently lose real network; it counts as major.
 * @param {import("ol/Feature").default} feature
 * @returns {string} one of the ROAD_CLASS_* constants
 */
export function roadClass(feature) {
  const highway = getPropertyEndingWith(feature, "-highway");
  if (!highway) return ROAD_CLASS_UNTAGGED;
  if (highway === ROAD_CLASS_SERVICE) return ROAD_CLASS_SERVICE;
  if (MINOR_ROAD_HIGHWAYS.includes(highway)) return ROAD_CLASS_MINOR;
  return ROAD_CLASS_MAJOR;
}

// Majors keep the pre-existing black/2 so nothing about a road that matters changes.
// Minors go very light and slightly thinner; service lands one tone darker than the
// rest so a driveway is still findable without pulling the eye. Widths are screen
// pixels. If the thinning turns out to be unnecessary, MINOR_WIDTH is the one dial.
const MAJOR_COLOR = "black";
const MAJOR_WIDTH = 2;
const SERVICE_COLOR = "#bdbdbd";
const MINOR_COLOR = "#e6e6e6";
const MINOR_WIDTH = 1.5;

// Draw order WITHIN a vector layer. OL sorts styles by zIndex, so where two ways cross
// the major is painted over the minor rather than whichever the source happened to
// yield last. Anything carrying actual restriction data outranks both.
export const Z_MINOR_ROAD = 1;
export const Z_MAJOR_ROAD = 2;
export const Z_RESTRICTION = 3;

/**
 * The plain stroke for a way with nothing to report, by class.
 * @param {string} cls one of the ROAD_CLASS_* constants
 * @returns {{color: string, width: number, zIndex: number}} plain JSON — this ends up
 *   inside a styleBySection descriptor, which is merged by JSON.stringify()
 */
export function roadClassStroke(cls) {
  // Untagged shares the dimmest treatment on the per-way styles. It is HIDDEN on the
  // lane layer, but the per-way layers keep it: a way whose basemap highway tag didn't
  // come through may still carry a restriction worth seeing, and the restriction colour
  // outranks this anyway.
  if (cls === ROAD_CLASS_MINOR || cls === ROAD_CLASS_UNTAGGED) {
    return { color: MINOR_COLOR, width: MINOR_WIDTH, zIndex: Z_MINOR_ROAD };
  }
  if (cls === ROAD_CLASS_SERVICE) {
    return { color: SERVICE_COLOR, width: MINOR_WIDTH, zIndex: Z_MINOR_ROAD };
  }
  return { color: MAJOR_COLOR, width: MAJOR_WIDTH, zIndex: Z_MAJOR_ROAD };
}
